import { ref } from 'vue';
import { instanceToInstance } from 'class-transformer';
import {
  InspectedBuilding,
  ProductEstimation,
} from '@/models/InspectedBuilding';
import { useInspectedBuildingStore } from '@/store/entities/inspectedBuildingStore';
import { useInspectionPointStore } from '@/store/entities/inspectionPointStore';
import { useRoomGroupStore } from '@/store/entities/roomGroupStore';
import { ProductEstimationTypesEnmu } from '@/enums/device';
import {
  InspectionPointsLocation,
  useInstallationLocationsTable,
} from '@/composables/useInstallationLocationsTable';
import { useFormStore } from '@/store/form/formStore';

export function useInspectedBuildingForm(
  node?: InspectedBuilding | undefined,
  buildingId?: string,
  imageCaptureRef?: any,
  emit?: any
) {
  const inspectedBuilding = ref(new InspectedBuilding());
  const installationLocations = ref<InspectionPointsLocation[]>([]);

  const getInspectedBuilding = (buildingId: string) => {
    const existingInspectedBuilding =
      useInspectedBuildingStore().getInspectedBuildingByBuildingId(buildingId);
    if (existingInspectedBuilding) {
      inspectedBuilding.value = instanceToInstance(existingInspectedBuilding);
      if (!Array.isArray(inspectedBuilding.value.productEstimations)) {
        inspectedBuilding.value.productEstimations = [];
      }
    }
    return inspectedBuilding.value;
  };

  const initializeInspectedBuildingForm = () => {
    if (!node?.buildingId) {
      inspectedBuilding.value.buildingId = buildingId as string;
      initEstimations();
    } else {
      inspectedBuilding.value = instanceToInstance(node);
      initEstimations();
      const installationLocationsArray = useInspectionPointStore()
        .getAllSortedLocationsByBuilding(node.buildingId)
        .map((item: any) => instanceToInstance(item));

      installationLocations.value =
        useInstallationLocationsTable().buildInspectionPointsArray(
          installationLocationsArray
        );
    }

    useFormStore().trackStateRefs([inspectedBuilding, installationLocations]);
  };

  const setupInspectedBuilding = () => {
    if (!buildingId) return;
    inspectedBuilding.value.buildingId = buildingId;
    initEstimations();
  };

  const upsertEstimation = (entry: ProductEstimation) => {
    const idx = inspectedBuilding.value.productEstimations.findIndex(
      (e) => e.id === entry.id
    );
    if (idx === -1) {
      inspectedBuilding.value.productEstimations.push(entry);
    } else {
      inspectedBuilding.value.productEstimations[idx] = entry;
    }
  };

  const initSrtEstimation = () => {
    if (!inspectedBuilding.value.buildingId) return;
    const systemSrtCount = useInspectedBuildingStore().getSystemSRTs(
      inspectedBuilding.value.buildingId
    );
    const srtCustomizedNumber = '42001';
    const existing = inspectedBuilding.value.productEstimations.find(
      (e) => e.customizedNumber === srtCustomizedNumber
    );
    let srtEntry;
    if (existing) {
      srtEntry = new ProductEstimation({
        id: existing.id,
        type: ProductEstimationTypesEnmu.SRT,
        customizedNumber: srtCustomizedNumber,
        count: systemSrtCount,
        estimation: existing?.estimation,
      });
    } else {
      srtEntry = new ProductEstimation({
        type: ProductEstimationTypesEnmu.SRT,
        customizedNumber: srtCustomizedNumber,
        count: systemSrtCount,
        estimation: undefined,
      });
    }

    upsertEstimation(srtEntry);
  };

  const initAdaptersEstimations = () => {
    const buildingId = inspectedBuilding.value?.buildingId;
    if (!buildingId) return;

    const roomGroupStore = useRoomGroupStore();
    const inspectionPointStore = useInspectionPointStore();

    const roomGroups =
      roomGroupStore.getRoomGroupsByBuildingId(buildingId) ?? [];

    const adapters = roomGroups.flatMap((rg) => {
      const ips =
        inspectionPointStore.getInspectionPointsByRoomGroupId(rg.id) ?? [];
      return ips.flatMap((p) => p.accessories ?? []);
    });
    if (adapters.length === 0) return;

    const counts = new Map<string, number>();
    for (const a of adapters) {
      const key = a?.customizedNumber;
      if (!key) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    if (counts.size === 0) return;

    const existingEstimations =
      inspectedBuilding.value.productEstimations ?? [];

    for (const [customizedNumber, count] of counts) {
      const existing = existingEstimations.find(
        (e) => e.customizedNumber === customizedNumber
      );

      let srtEntry;

      if (existing) {
        srtEntry = new ProductEstimation({
          id: existing.id,
          type: ProductEstimationTypesEnmu.ADAPTER,
          customizedNumber,
          count,
          estimation: existing?.estimation,
        });
      } else {
        srtEntry = new ProductEstimation({
          type: ProductEstimationTypesEnmu.ADAPTER,
          customizedNumber,
          count,
          estimation: undefined,
        });
      }

      upsertEstimation(srtEntry);
    }
  };

  const initEstimations = () => {
    initSrtEstimation();
    initAdaptersEstimations();
  };

  const handleSubmit = () => {
    emit('update-entities', [
      inspectedBuilding.value,
      ...installationLocations.value.map((loc) => loc.inspectionPoint),
    ]);

    if (imageCaptureRef.value) {
      imageCaptureRef.value.saveImage();
    }
  };

  const onUpdateLocation = (
    index: number,
    updatedLocation: InspectionPointsLocation
  ) => {
    if (!installationLocations.value) return [updatedLocation];
    const updatedInstallationLocations = installationLocations.value.slice();
    updatedInstallationLocations[index] = updatedLocation;

    return updatedInstallationLocations;
  };

  return {
    inspectedBuilding,
    installationLocations,
    getInspectedBuilding,
    initializeInspectedBuildingForm,
    setupInspectedBuilding,
    handleSubmit,
    onUpdateLocation,
  };
}
